import { FC, memo, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { handleFetchResponse } from 'api/errors'
import ModalForm from 'components/ModalForm/ModalForm'
import ModalItem from 'components/ModalItem'
import { MODAL_IDS, useModal } from 'components/ModalProvider'
import { Form } from 'forms/Form'
import { useActions } from 'hooks'
import { useUserInfoQuery } from 'modules/login/api'
import { OrderCreateCustomer } from 'schema'
import { changeFields } from 'shared/forms/mutators'

import { useCreateOrderMutation } from '../../api'
import { USER_INFO_NODE_ID } from '../../components/ModalUserInfo/ModalUserInfo'
import { OrderCreateAgreeOffer } from '../../components/OrderCreate/OrderCreateAgreeOffer'
import { prepareOrderCreateRequest } from '../../helpers/order'
import { orderDetailRoute, orderRoute } from '../../routes'
import { TOrder } from '../../types'
import { TOrderCreateFormData } from '../../types/orderCreateTypes'
import { decorators, resetTerminalSyncStates } from '../OrderCreateRoute/decorators'
import OrderCreateForm from '../OrderCreateRoute/OrderCreateForm'
import { getFullInfoPayload } from './helpers'

type OrderCopyFormProps = {
  initial?: TOrderCreateFormData
  onFailure?: () => void
  onSuccess?: () => void
}

export const OrderCopyForm: FC<OrderCopyFormProps> = ({ initial, onFailure, onSuccess }) => {
  const isFirstRender = useRef(true)
  const [isCompleteUserName, setIsCompleteUserName] = useState(true)
  const [isCompleteUserDocument, setIsCompleteUserDocument] = useState(true)
  const [isShowUserInfoError, setIsShowUserInfoError] = useState(false)

  const navigate = useNavigate()
  const { closeModal } = useModal()
  const { setFullInfo, clearOrderAddStore } = useActions()
  const [createOrder, { isLoading }] = useCreateOrderMutation()
  const {
    data: user,
    refetch: refetchUser,
    isLoading: isLoadingUserInfo,
    isFetching: isFetchingUserInfo
  } = useUserInfoQuery(undefined, { refetchOnMountOrArgChange: true })
  const isCompleteUser = Boolean(user?.firstname && user?.lastname)

  useEffect(() => {
    if (!initial) {
      return
    }

    const { departurePoint, destinationPoint, customer } = initial

    setFullInfo(
      getFullInfoPayload({
        customer,
        // @ts-ignore
        departurePoint,
        // @ts-ignore
        destinationPoint
      })
    )

    return () => {
      clearOrderAddStore()
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => () => resetTerminalSyncStates(), [])

  const handleSuccess = useCallback(
    (response: { data: { order: TOrder } }) => {
      const { order } = response.data

      onSuccess?.()
      closeModal(MODAL_IDS.CreateOrderForm)
      navigate({ to: orderDetailRoute.to, params: { id: order.id } })
    },
    [closeModal, navigate, onSuccess]
  )

  const handleFailure = useCallback(() => {
    onFailure?.()
  }, [onFailure])

  const scrollToUserInfo = useCallback(() => {
    const containerNode = document.querySelector(`#${USER_INFO_NODE_ID}`)
    containerNode?.scrollIntoView()
  }, [])

  const handleSubmit = useCallback(
    async (data: TOrderCreateFormData) => {
      const { data: userRefetch } = await refetchUser()
      const isCompleteUserName = Boolean(userRefetch?.firstname && userRefetch?.lastname)
      const isCompleteUserDocument =
        Boolean(userRefetch?.document?.length) || data.customer?.type === OrderCreateCustomer.type.LEGAL
      const isCompleteUserRefetch = isCompleteUserName && isCompleteUserDocument

      if (isCompleteUserRefetch) {
        return createOrder(prepareOrderCreateRequest(data)).then(handleFetchResponse(handleSuccess, handleFailure))
      }

      setIsShowUserInfoError(true)
      setIsCompleteUserName(isCompleteUserName)
      setIsCompleteUserDocument(isCompleteUserDocument)
      setTimeout(scrollToUserInfo)
    },
    [createOrder, handleFailure, handleSuccess, refetchUser, scrollToUserInfo]
  )

  const handleClose = useCallback(() => {
    navigate(orderRoute)
    closeModal(MODAL_IDS.CreateOrderForm)
  }, [closeModal, navigate])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false

      return
    }

    if (!(isLoadingUserInfo || isFetchingUserInfo) && !isCompleteUser) {
      setIsShowUserInfoError(true)
    }
  }, [closeModal, isCompleteUser, isFetchingUserInfo, isLoadingUserInfo])

  return (
    <>
      <ModalItem isOpen id={MODAL_IDS.CreateOrderForm}>
        <Form<TOrderCreateFormData>
          keepDirtyOnReinitialize
          decorators={decorators}
          initialValues={initial}
          mutators={{ changeFields }}
          name="copyOrderForm"
          onSubmit={handleSubmit}
          render={(formProps) => (
            <form name="copyOrderForm" onSubmit={formProps.handleSubmit}>
              <ModalForm
                isStickyFooter
                isStickyHeader
                buttonLabel="Заказать"
                footerBefore={<OrderCreateAgreeOffer />}
                footerClassName="flex"
                id={MODAL_IDS.CreateOrderForm}
                isLoading={isLoading}
                onClose={handleClose}
                size="xl"
                title="Оформление заявки"
              >
                <OrderCreateForm
                  isCompleteUserDocument={isCompleteUserDocument}
                  isCompleteUserName={isCompleteUserName}
                  isShowUserInfoError={isShowUserInfoError}
                  user={user}
                />
              </ModalForm>
            </form>
          )}
        />
      </ModalItem>
    </>
  )
}

export default memo(OrderCopyForm)
